import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Calendar, Clock, MapPin } from "lucide-react"
import { ProfileLayout } from "../components/ProfileLayout"
import api from "../utils/api"

// Helper function to convert string to Title Case
function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function MyBookingsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "")
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all")
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const userStr = localStorage.getItem("user")
        if (!userStr) return

        const user = JSON.parse(userStr)
        let mobile = user?.mobile || user?.phone || ""
        if (!mobile) return

        if (!mobile.startsWith("91")) {
          mobile = `91${mobile}`
        }

        const response = await api.get(`/apis/web/myappointments?mobile=${mobile}`)
        
        if (response.data?.result && Array.isArray(response.data.result)) {
          const mapped = response.data.result.map((item, index) => {
            let formattedTime = ""
            if (item.APPOINTMENTTIME) {
              const d = new Date(item.APPOINTMENTTIME)
              if (!isNaN(d.getTime())) {
                const hours = d.getHours()
                const minutes = d.getMinutes()
                const ampm = hours >= 12 ? "PM" : "AM"
                formattedTime = `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${ampm}`
              }
            }

            return {
              id: item.APPOINTMENTNO || index + 1,
              patientName: toTitleCase(item.PATIENT_NAME || "Patient"),
              doctorName: toTitleCase(item.DOCTNAME || "Doctor"),
              department: toTitleCase(item.DEPTNAME || "General"),
              date: item.APPOINTMENTDATE || "",
              time: formattedTime,
              location: item.HospitalName || "Pranaam Hospital",
              bill: `₹${item.VISITAMOUNT || 0}`,
              status: item.CONSULTED === "Y" ? "completed" : (item.VISITSTATUS || "confirmed").toLowerCase()
            }
          })
          setBookings(mapped)
        }
      } catch (error) {
        console.error("Error fetching bookings:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchBookings()
  }, [])

  useEffect(() => {
    const params = {}
    if (searchQuery) params.search = searchQuery
    if (statusFilter !== "all") params.status = statusFilter
    setSearchParams(params)
  }, [searchQuery, statusFilter, setSearchParams])

  const filteredBookings = bookings.filter(booking => {
    const locationFilter = searchParams.get("location")
    const specialtyFilter = searchParams.get("specialty")
    const doctorFilter = searchParams.get("doctor")
    
    const matchesLocation = !locationFilter || booking.location === locationFilter
    const matchesSpecialty = !specialtyFilter || booking.department.toLowerCase().includes(specialtyFilter.toLowerCase())
    const matchesDoctor = !doctorFilter || booking.doctorName.toLowerCase().includes(doctorFilter.toLowerCase())
    
    const matchesSearch = 
      booking.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.doctorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.department.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter
    
    return matchesLocation && matchesSpecialty && matchesDoctor && matchesSearch && matchesStatus
  })

  const getStatusColor = (status) => {
    switch(status) {
      case "confirmed": return "bg-green-50 text-green-700 border-green-200"
      case "pending": return "bg-yellow-50 text-yellow-700 border-yellow-200"
      case "completed": return "bg-blue-50 text-blue-700 border-blue-200"
      case "cancelled": return "bg-red-50 text-red-700 border-red-200"
      default: return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900">My Bookings</h1>
          </div>
        </div>

        <div className="px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading your bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-gray-500 font-medium">No bookings found.</div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{booking.patientName}</h3>
                      <p className="text-sm text-gray-600">{booking.doctorName} | {booking.department}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-md text-sm font-medium border ${getStatusColor(booking.status)}`}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{booking.date}</span>
                    <Clock className="w-4 h-4 ml-4" />
                    <span>{booking.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{booking.location}</span>
                    <span className="ml-auto font-medium text-gray-900">Bill: {booking.bill}</span>
                  </div>
                </div>
              </div>
            ))}
            </div>
            )}
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}
